import { Application, Container } from 'pixi.js';
import { campaign, makeT, maps, pickLocale, resolveMap, themes, type Locale } from '@holdline/content';
import {
  challengeById,
  challengeSeedLabel,
  dailySeedLabel,
  defaultBalance,
  defaultProfile,
  ladderLength,
  ladderPosition,
  ladderUnlocked,
  levelOfMap,
  levelRef,
  snapshotOf,
  weekKey,
  type ArchetypeId,
  type ChallengeTemplate,
  type ProductId,
  type Profile,
  type RunState,
  type SkinId,
} from '@holdline/sim';
import { Api, devUserName, type PurchaseResult } from './api';
import { art } from './art';
import { Game } from './game';
import { L } from './layout';
import { pickLanguage, storeLocale, storedLocale } from './lang';
import { Menu } from './menu';
import { music } from './music';
import { sound } from './sound';
import { tgWebApp } from './telegram';
import { View } from './view';

async function boot(): Promise<void> {
  const rawTg = window.Telegram?.WebApp;
  rawTg?.ready?.();
  rawTg?.expand?.();
  rawTg?.disableVerticalSwipes?.();
  rawTg?.setHeaderColor?.('#0f1115');
  rawTg?.setBackgroundColor?.('#0f1115');
  const tg = tgWebApp();

  const app = new Application();
  await app.init({
    resizeTo: window,
    background: 0x0f1115,
    antialias: true,
    resolution: Math.min(window.devicePixelRatio || 1, 2),
    autoDensity: true,
  });
  document.body.appendChild(app.canvas);

  const params = new URLSearchParams(location.search);
  // Language: URL wins, then the device's remembered choice, else ask once before anything else (Telegram's
  // language only pre-selects the answer). `?lang=` also becomes the remembered choice.
  const stageRoot = new Container();
  app.stage.addChild(stageRoot);
  const fitStage = (): void => {
    const scale = Math.min(app.screen.width / L.W, app.screen.height / L.H);
    stageRoot.scale.set(scale);
    stageRoot.x = Math.floor((app.screen.width - L.W * scale) / 2);
    stageRoot.y = Math.floor((app.screen.height - L.H * scale) / 2);
  };
  fitStage();
  app.renderer.on('resize', fitStage);
  const suggested = pickLocale(tg?.initDataUnsafe.user?.language_code ?? navigator.language);
  let locale: Locale;
  const langParam = params.get('lang');
  if (langParam) {
    locale = pickLocale(langParam);
    storeLocale(locale);
  } else locale = storedLocale() ?? (await pickLanguage(app, stageRoot, suggested));
  const themeId = params.get('theme') ?? 'bunker';
  const theme = themes[themeId] ?? themes['bunker']!;
  const t = makeT(locale);
  // A weekly challenge: its own map, modifiers and seed; results go to /api/challenge/run instead of the daily board.
  const week = weekKey();
  const challengeId = params.get('challenge');
  const challenge: ChallengeTemplate | null = challengeId ? challengeById(week, challengeId, defaultBalance) : null;
  const requestedMap = challenge?.mapId ?? params.get('map');

  const apiBase = (import.meta.env['VITE_API_URL'] as string | undefined) ?? '';
  const api = new Api(apiBase, tg?.initData ?? '', tg ? null : devUserName());
  let botUsername: string | null = null;
  api
    .daily('map01')
    .then((d) => {
      botUsername = d.botUsername;
    })
    .catch(() => {
      /* offline is fine, the game runs locally */
    });

  // Bitmap art pack, if one exists for the theme (or ?art=<pack> to compare candidates); vectors otherwise.
  const artLoad = art.load(params.get('art') ?? themeId);

  // The run starts at the profile's levels, so wait briefly for the server; offline plays at base levels.
  let profile: Profile = defaultProfile();
  let profileLoaded = false;
  try {
    const info = await Promise.race([
      api.profile(),
      new Promise<never>((_, reject) => setTimeout(() => reject(new Error('timeout')), 1500)),
    ]);
    profile = info.profile;
    profileLoaded = true;
  } catch {
    /* offline or slow: base levels, the server would reject anything higher anyway */
  }

  await artLoad;
  // Which map: the challenge's, the requested one, else where the player stands on the ladder.
  const hereRef = levelRef(ladderPosition(profile, defaultBalance), defaultBalance);
  const mapId = (requestedMap && resolveMap(requestedMap) ? requestedMap : null) ?? hereRef?.mapId ?? 'l1';
  const map = resolveMap(mapId) ?? resolveMap('l1')!;

  // Daily seed for the shared leaderboard; ?seed= overrides for testing (and skips submission).
  const daily = dailySeedLabel(map.id);
  const seedLabel = challenge ? challengeSeedLabel(week, challenge.id) : (params.get('seed') ?? daily);
  const isDaily = !challenge && seedLabel === daily;
  const levelHere = levelOfMap(map.id, defaultBalance);

  let viewRef: View | null = null;
  const game = new Game(map, seedLabel, snapshotOf(profile), challenge?.modifiers ?? []);

  /** The server answered with a newer profile: remember it and let an untouched run restart on it. */
  const applyProfile = (p: Profile): void => {
    const skinBefore = profile.shop.skin;
    profile = p;
    if (p.shop.skin !== skinBefore) viewRef?.reskin();
    game.snapshot = snapshotOf(p);
    if (game.state.phase === 'placing' && game.state.actions.length === 0) game.restart();
  };
  const gearCall = async (action: 'equip' | 'unequip' | 'scrap' | 'merge', body: Parameters<Api['gear']>[1]): Promise<void> => {
    const r = await api.gear(action, body);
    applyProfile(r.profile);
  };
  const sleep = (ms: number): Promise<void> => new Promise((r) => setTimeout(r, ms));
  /** Buy one product: the bot issues a Stars invoice, Telegram takes the payment, the webhook fulfils, we poll. */
  const buy = async (product: ProductId, arch?: ArchetypeId, skin?: SkinId): Promise<PurchaseResult | null> => {
    const inv = await api.invoice(product, arch, skin);
    if (inv.dev) {
      const r = await api.devPay(inv.orderId);
      applyProfile(r.profile);
      return r.result;
    }
    if (!inv.link || !tg?.openInvoice) throw new Error('payments_off');
    const link = inv.link;
    const status = await new Promise<string>((resolve) => tg.openInvoice!(link, resolve));
    if (status === 'cancelled') return null;
    if (status === 'failed') throw new Error('payment_failed');
    for (let i = 0; i < 25; i++) {
      const o = await api.order(inv.orderId);
      if (o.status === 'paid' && o.result) {
        applyProfile(o.profile);
        return o.result;
      }
      await sleep(800);
    }
    throw new Error('payment_pending');
  };

  // Campaign progress lives in the browser until the player profile moves to the server.
  const wonKey = (id: string): string => `holdline.won.${id}`;
  const hasWon = (id: string): boolean => {
    try {
      return localStorage.getItem(wonKey(id)) === '1';
    } catch {
      return false;
    }
  };
  const menu = new Menu(
    theme,
    locale,
    game.balance,
    t,
    {
      profile: () => profile,
      gear: {
        equip: (itemId) => gearCall('equip', { itemId }),
        unequip: (slot) => gearCall('unequip', { slot }),
        scrap: (itemId) => gearCall('scrap', { itemId }),
        merge: (slot, rarity) => gearCall('merge', { slot, rarity }),
      },
      hero: { select: async (heroId) => applyProfile((await api.selectHero(heroId)).profile) },
      skills: { upgrade: async (skillId) => applyProfile((await api.upgradeSkill(skillId)).profile) },
      daily: (mapId) => api.daily(mapId),
      board: (mapId, period) => api.board(mapId, period),
      challenges: () => api.challenges(),
      referral: () => api.referral(),
      onSwitchLanguage: () => {
        storeLocale(locale === 'ru' ? 'en' : 'ru');
        const next = new URLSearchParams(location.search);
        next.delete('lang');
        location.search = next.toString();
      },
      onInvite: (link) => {
        const text = t('invite_text', { title: theme.title[locale] });
        if (tg) tg.openTelegramLink(`https://t.me/share/url?url=${encodeURIComponent(link)}&text=${encodeURIComponent(text)}`);
        else if (navigator.share) navigator.share({ text, url: link }).catch(() => {});
        else navigator.clipboard?.writeText(`${text} ${link}`).then(() => view.showToast(t('copied'))).catch(() => {});
      },
      onPlayChallenge: (id) => {
        const next = new URLSearchParams(location.search);
        next.set('challenge', id);
        next.delete('map');
        next.delete('seed');
        location.search = next.toString();
      },
      challengeId: challenge?.id ?? null,
      quests: () => api.quests(),
      shop: {
        info: () => api.shop(),
        consent: async () => applyProfile((await api.consent()).profile),
        freeChest: async () => {
          const r = await api.freeChest();
          applyProfile(r.profile);
          if (r.quests) menu.setQuests(r.quests.list);
          return r.result;
        },
        buy,
        selectSkin: async (skin) => {
          applyProfile((await api.selectSkin(skin)).profile);
          view.reskin();
        },
      },
      maps: () =>
        campaign
          .filter((id) => maps[id]!.endless)
          .map((id) => {
            const def = maps[id]!;
            const req = def.requires;
            return {
              id,
              def,
              unlocked: !req || profile.maps[req]?.won === true || hasWon(req),
              current: id === map.id,
            };
          }),
      ladder: () => {
        const total = ladderLength(defaultBalance);
        const position = ladderPosition(profile, defaultBalance);
        const chapters = defaultBalance.ladder.chapters.map((c, ci) => {
          const first = defaultBalance.ladder.chapters.slice(0, ci).reduce((a, x) => a + x.levels, 0) + 1;
          const levels = [];
          for (let n = first; n < first + c.levels; n++) {
            const ref = levelRef(n, defaultBalance)!;
            const prog = profile.maps[ref.mapId];
            levels.push({ n, mapId: ref.mapId, finale: ref.finale, unlocked: ladderUnlocked(profile, n, defaultBalance), won: prog?.won === true, stars: prog?.stars ?? 0, current: ref.mapId === map.id });
          }
          return { id: c.id, chapter: ci + 1, roster: c.roster, finale: c.finale ?? null, levels };
        });
        return { total, position, chapters };
      },
      onSelectMap: (id) => {
        const next = new URLSearchParams(location.search);
        next.set('map', id);
        next.delete('seed');
        next.delete('challenge');
        location.search = next.toString();
      },
    },
    () => {},
  );
  const view = new View(game, theme, locale, t, {
    onMenu: () => menu.open('maps'),
    levelNumber: levelHere?.n ?? null,
    skin: () => profile.shop.skin,
    onNextLevel: (mapId) => {
      const next = new URLSearchParams(location.search);
      next.set('map', mapId);
      next.delete('seed');
      next.delete('challenge');
      location.search = next.toString();
    },
    challengeName: challenge ? theme.challenges[challenge.id]?.name[locale] ?? challenge.id : null,
    onLeaveChallenge: () => {
      const next = new URLSearchParams(location.search);
      next.delete('challenge');
      location.search = next.toString();
    },
    canShare: () => Boolean(tg || navigator.share || navigator.clipboard),
    onShare: () => {
      const s = game.state;
      const waves = s.phase === 'won' ? s.totalWaves : s.wave - 1;
      const text = t('share_text', { n: waves, score: game.score(), title: theme.title[locale] });
      const url = botUsername ? `https://t.me/${botUsername}` : location.origin;
      if (tg) {
        tg.openTelegramLink(`https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`);
      } else if (navigator.share) {
        navigator.share({ text, url }).catch(() => {});
      } else {
        navigator.clipboard?.writeText(`${text} ${url}`).then(() => view.showToast(t('copied'))).catch(() => {});
      }
    },
  });
  viewRef = view;
  stageRoot.addChild(view.root, menu.root);

  // Submit each finished run exactly once. The server replays it and answers with the rank.
  let submittedFor: RunState | null = null;
  game.on((e) => {
    if (e.kind !== 'state') return;
    const s = game.state;
    if ((s.phase !== 'won' && s.phase !== 'lost') || submittedFor === s) return;
    submittedFor = s;
    if (s.phase === 'won') {
      try {
        localStorage.setItem(wonKey(map.id), '1');
      } catch {
        /* private mode, fine */
      }
    }
    if (challenge) {
      view.setSubmission({ kind: 'loading' });
      api
        .submitChallenge({ ...game.submission(), challengeId: challenge.id })
        .then((result) => {
          applyProfile(result.profile);
          if (game.state === s) view.setSubmission({ kind: 'challenge', result });
        })
        .catch(() => {
          if (game.state === s) view.setSubmission({ kind: 'error' });
        });
      return;
    }
    if (!isDaily) return;
    view.setSubmission({ kind: 'loading' });
    api
      .submit(game.submission())
      .then((result) => {
        // The server is the source of truth for progress; the next run starts at the new levels.
        applyProfile(result.profile);
        if (result.quests) menu.setQuests(result.quests.list);
        if (game.state === s) view.setSubmission({ kind: 'ready', result });
      })
      .catch(() => {
        if (game.state === s) view.setSubmission({ kind: 'error' });
      });
  });

  if (!profileLoaded) view.showToast(t('profile_offline'));

  // First visit: three lines on how to play, then the coach points at what to drag where. ?coach=1 replays it.
  const forceCoach = params.get('coach') === '1';
  try {
    if (!localStorage.getItem('holdline.intro') || forceCoach) {
      view.showIntro(() => {
        try {
          localStorage.setItem('holdline.intro', '1');
        } catch {
          /* fine */
        }
        view.startCoach(forceCoach);
      });
    } else view.startCoach();
  } catch {
    view.startCoach();
  }

  app.ticker.add((ticker) => {
    game.update(ticker.deltaMS);
    view.tick(ticker.deltaMS);
  });

  // Handy for poking at the sim, the renderer and the Pixi app from the console (and from tools/drive.mjs).
  const dbg = window as unknown as { game: Game; view: View; menu: Menu; app: Application; music: typeof music; sound: typeof sound };
  dbg.game = game;
  dbg.view = view;
  dbg.menu = menu;
  dbg.app = app;
  dbg.music = music;
  dbg.sound = sound;
}

boot().catch((err: unknown) => {
  console.error(err);
  document.body.innerHTML = `<pre style="color:#f55;padding:16px">${String(err)}</pre>`;
});
